// services/sharedSessionService.ts - Phase 2: Sharing with Limits
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  Segment,
  VideoSettings,
  SharedSession,
  SHARE_LIMITS,
  ShareDeactivationReason,
  CreateShareResponse,
  normalizeVideoSettingsViewCrop
} from '../types';
import { normalizeCombos } from '../utils';
import { syncBackendShareCounters } from './userProfileFunctions';

const SHARE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{3,120}$/;
const MAX_SHARE_ID_ATTEMPTS = 8;
const MEDIA_ACCESS_TOKEN_TTL_MS = 2 * 60 * 60 * 1000;

/**
 * Generate a URL-friendly share ID based on video title
 * Example: "My Bachata Routine" -> "my-bachata-routine-x7k2"
 */
function generateTitleBasedShareId(videoTitle: string): string {
  const slug = videoTitle
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 40);

  const suffix = Math.random().toString(36).substring(2, 6);
  return slug ? `${slug}-${suffix}` : `dance-video-${suffix}`;
}

const generateMediaAccessToken = (): string => {
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += Math.floor(Math.random() * 16).toString(16);
  }
  return token;
};

const isValidShareId = (shareId: string): boolean => {
  return SHARE_ID_PATTERN.test(shareId);
};

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybeTimestamp = value as { toDate?: () => Date };
    if (typeof maybeTimestamp.toDate === 'function') {
      const converted = maybeTimestamp.toDate();
      if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
        return converted;
      }
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

const buildShareErrorMessage = (reason: unknown, maxViews: number): string => {
  if (reason === 'expired') {
    return 'This share link has expired (7 days maximum)';
  }
  if (reason === 'max_views') {
    return `This share has reached its view limit (${maxViews} views maximum)`;
  }
  if (reason === 'user_revoked') {
    return 'This share link has been revoked by the owner';
  }
  return 'This share link is no longer active';
};

const getFunctionsBaseUrl = (): string => {
  const value = process.env.REACT_APP_FUNCTIONS_URL?.trim();
  if (!value) {
    throw new Error('Share media service is unavailable. Please try again shortly.');
  }
  return value.replace(/\/$/, '');
};

const buildSharedVideoProxyUrl = (
  functionsBaseUrl: string,
  shareId: string,
  mediaAccessToken: string
): string => {
  return `${functionsBaseUrl}/streamSharedVideo?share=${encodeURIComponent(shareId)}&access=${encodeURIComponent(mediaAccessToken)}`;
};

export const reconcileActiveSharesCount = async (userId: string): Promise<number> => {
  const counters = await syncBackendShareCounters(userId);
  return counters.activeSharesCount;
};

const buildUniqueShareId = async (videoTitle: string): Promise<string> => {
  const baseId = generateTitleBasedShareId(videoTitle);

  for (let attempt = 0; attempt < MAX_SHARE_ID_ATTEMPTS; attempt++) {
    const candidate =
      attempt === 0
        ? baseId
        : `${baseId}-${Math.random().toString(36).substring(2, 6)}`;

    const existingDoc = await getDoc(doc(db, 'sharedSessions', candidate));
    if (!existingDoc.exists()) {
      return candidate;
    }
  }

  throw new Error('Failed to generate a unique share link ID. Please retry.');
};

/**
 * Create a shareable session with limits:
 * - 30 views maximum
 * - 7 days expiration
 * - 1 active share per user (deactivates old ones)
 */
export const createSharedSession = async (
  videoId: string,
  videoStoragePath: string,
  videoTitle: string,
  segments: Segment[],
  settings: VideoSettings,
  userId: string
): Promise<CreateShareResponse> => {
  try {
    console.log('🟡 Creating shared session with limits...');
    console.log('Video storage path:', videoStoragePath);

    // STEP 1: Reconcile count before enforcing active-share limit
    const activeCount = await reconcileActiveSharesCount(userId);

    if (activeCount >= SHARE_LIMITS.MAX_ACTIVE_SHARES_PER_USER) {
      console.log('🟡 User has max active shares, deactivating old ones...');
      await deactivateUserShares(userId);
    }

    // STEP 2: Generate unique share ID with bounded retries
    const shareId = await buildUniqueShareId(videoTitle);

    // STEP 3: Calculate expiration (7 days from now)
    const createdAt = new Date();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + SHARE_LIMITS.SHARE_EXPIRY_DAYS);

    // STEP 4: Create shared session with limits
    const normalizedSegments = normalizeCombos(segments);
    const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
    const sharedSession = {
      ownerId: userId,
      videoId,
      videoStoragePath,
      title: videoTitle,
      segments: normalizedSegments,
      settings: normalizedSettings,
      createdAt: Timestamp.fromMillis(createdAt.getTime()),
      expiresAt: Timestamp.fromMillis(expiresAt.getTime()),
      accessCount: 0,
      maxViews: SHARE_LIMITS.MAX_VIEWS_PER_SHARE,
      isActive: true
    };

    // STEP 5: Save to Firestore
    await setDoc(doc(db, 'sharedSessions', shareId), sharedSession);

    // STEP 6: Update backend-owned user share statistics with reconciled counts
    await reconcileActiveSharesCount(userId);

    console.log('🟢 Shared session created with limits:', shareId);
    console.log(`   Max views: ${SHARE_LIMITS.MAX_VIEWS_PER_SHARE}`);
    console.log(`   Expires: ${expiresAt.toLocaleDateString()}`);

    return {
      shareId,
      shareUrl: `${window.location.origin}/?share=${shareId}`,
      expiresAt,
      maxViews: SHARE_LIMITS.MAX_VIEWS_PER_SHARE,
      createdAt
    };
  } catch (error) {
    console.error('🔴 Error creating shared session:', error);
    throw new Error(
      'Failed to create shareable link: ' +
        (error instanceof Error ? error.message : 'Unknown error')
    );
  }
};

/**
 * Load a shared session by ID
 * Validates limits and increments view count atomically
 */
export const loadSharedSession = async (
  shareId: string
): Promise<SharedSession & { videoUrl: string }> => {
  try {
    console.log('🟡 Loading shared session:', shareId);

    const normalizedShareId = shareId.trim();
    if (!isValidShareId(normalizedShareId)) {
      throw new Error('Invalid share link format');
    }

    const functionsBaseUrl = getFunctionsBaseUrl();
    const shareRef = doc(db, 'sharedSessions', normalizedShareId);

    const result = await runTransaction(db, async (transaction) => {
      const shareDoc = await transaction.get(shareRef);

      if (!shareDoc.exists()) {
        return {
          status: 'blocked' as const,
          reason: 'not_found' as const,
          message: 'Share link not found'
        };
      }

      const data = shareDoc.data();
      const maxViews = data.maxViews || SHARE_LIMITS.MAX_VIEWS_PER_SHARE;

      if (!data.isActive) {
        return {
          status: 'blocked' as const,
          reason: 'inactive' as const,
          message: buildShareErrorMessage(data.deactivatedReason, maxViews)
        };
      }

      const expiresAt = toDate(data.expiresAt);
      if (!expiresAt || expiresAt.getTime() < Date.now()) {
        return {
          status: 'blocked' as const,
          reason: 'expired' as const,
          message: 'This share link has expired (7 days maximum)'
        };
      }

      const currentViews = data.accessCount || 0;
      if (currentViews >= maxViews) {
        return {
          status: 'blocked' as const,
          reason: 'max_views' as const,
          message: `This share has reached its view limit (${maxViews} views maximum)`
        };
      }

      const newViewCount = currentViews + 1;
      const reachedMaxViews = newViewCount >= maxViews;
      const mediaAccessToken = generateMediaAccessToken();
      const mediaAccessTokenExpiresAt = Timestamp.fromMillis(
        Date.now() + MEDIA_ACCESS_TOKEN_TTL_MS
      );

      const updates: { [key: string]: any } = {
        accessCount: newViewCount,
        lastAccessedAt: Timestamp.now(),
        mediaAccessToken,
        mediaAccessTokenExpiresAt
      };

      if (reachedMaxViews) {
        updates.isActive = false;
        updates.deactivatedReason = 'max_views';
        updates.deactivatedAt = Timestamp.now();
      }

      transaction.update(shareRef, updates);

      const createdAt = toDate(data.createdAt) || new Date();

      return {
        status: 'ok' as const,
        share: {
          ownerId: data.ownerId,
          videoId: data.videoId,
          videoStoragePath: data.videoStoragePath,
          title: data.title || data.videoTitle || 'Untitled',
          segments: normalizeCombos(data.segments || []),
          settings: normalizeVideoSettingsViewCrop(data.settings || {}) as VideoSettings,
          createdAt,
          expiresAt,
          accessCount: newViewCount,
          maxViews,
          isActive: !reachedMaxViews,
          mediaAccessToken
        }
      };
    });

    if (result.status !== 'ok') {
      if (result.reason === 'expired' || result.reason === 'max_views') {
        const deactivateReason: ShareDeactivationReason =
          result.reason === 'expired' ? 'expired' : 'max_views';
        try {
          await deactivateShare(normalizedShareId, deactivateReason);
        } catch (deactivationError) {
          console.warn('Unable to mark blocked share inactive:', deactivationError);
        }
      }

      throw new Error(result.message);
    }

    const videoUrl = buildSharedVideoProxyUrl(
      functionsBaseUrl,
      normalizedShareId,
      result.share.mediaAccessToken
    );

    console.log(
      `🟢 Share accessed: ${result.share.accessCount}/${result.share.maxViews} views`
    );

    return {
      shareId: normalizedShareId,
      ownerId: result.share.ownerId,
      videoId: result.share.videoId,
      videoStoragePath: result.share.videoStoragePath,
      title: result.share.title,
      segments: result.share.segments,
      settings: result.share.settings,
      createdAt: result.share.createdAt,
      expiresAt: result.share.expiresAt,
      accessCount: result.share.accessCount,
      maxViews: result.share.maxViews,
      isActive: result.share.isActive,
      videoUrl
    };
  } catch (error) {
    console.error('🔴 Error loading shared session:', error);
    throw error;
  }
};

/**
 * Deactivate a specific share
 */
export const deactivateShare = async (
  shareId: string,
  reason: ShareDeactivationReason
): Promise<void> => {
  try {
    console.log(`🟡 Deactivating share ${shareId} - reason: ${reason}`);

    const shareRef = doc(db, 'sharedSessions', shareId);
    const shareDoc = await getDoc(shareRef);

    if (!shareDoc.exists()) {
      console.log('⏭️ Share not found, skipping deactivation');
      return;
    }

    const data = shareDoc.data();

    await updateDoc(shareRef, {
      isActive: false,
      deactivatedReason: reason,
      deactivatedAt: Timestamp.now(),
      mediaAccessToken: deleteField(),
      mediaAccessTokenExpiresAt: deleteField()
    });

    if (typeof data.ownerId === 'string' && data.ownerId) {
      try {
        await reconcileActiveSharesCount(data.ownerId);
      } catch (counterError) {
        console.warn('Unable to sync share counters after deactivation:', counterError);
      }
    }

    console.log('🟢 Share deactivated successfully');
  } catch (error) {
    console.error('🔴 Error deactivating share:', error);
    throw error;
  }
};

/**
 * Deactivate all active shares for a user
 * Called when user creates a new share (1 active share limit)
 */
export const deactivateUserShares = async (userId: string): Promise<number> => {
  try {
    console.log('🟡 Deactivating all active shares for user:', userId);

    const sharesRef = collection(db, 'sharedSessions');
    const q = query(
      sharesRef,
      where('ownerId', '==', userId),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      await reconcileActiveSharesCount(userId);
      console.log('⏭️ No active shares found');
      return 0;
    }

    const batch = writeBatch(db);
    let count = 0;

    querySnapshot.forEach((shareDoc) => {
      batch.update(shareDoc.ref, {
        isActive: false,
        deactivatedReason: 'user_revoked' as ShareDeactivationReason,
        deactivatedAt: Timestamp.now(),
        mediaAccessToken: deleteField(),
        mediaAccessTokenExpiresAt: deleteField()
      });
      count++;
    });

    await batch.commit();
    await reconcileActiveSharesCount(userId);

    console.log(`🟢 Deactivated ${count} active shares`);
    return count;
  } catch (error) {
    console.error('🔴 Error deactivating user shares:', error);
    return 0;
  }
};

/**
 * Clean up expired shares (call periodically)
 */
export const cleanupExpiredShares = async (): Promise<number> => {
  try {
    console.log('🟡 Cleaning up expired shares...');

    const now = Timestamp.now();
    const sharesRef = collection(db, 'sharedSessions');
    const q = query(
      sharesRef,
      where('expiresAt', '<', now),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('⏭️ No expired shares found');
      return 0;
    }

    const batch = writeBatch(db);
    let count = 0;
    const ownersToReconcile = new Set<string>();

    querySnapshot.forEach((shareDoc) => {
      const data = shareDoc.data();
      if (typeof data.ownerId === 'string' && data.ownerId) {
        ownersToReconcile.add(data.ownerId);
      }

      batch.update(shareDoc.ref, {
        isActive: false,
        deactivatedReason: 'expired' as ShareDeactivationReason,
        deactivatedAt: Timestamp.now(),
        mediaAccessToken: deleteField(),
        mediaAccessTokenExpiresAt: deleteField()
      });
      count++;
    });

    await batch.commit();
    await Promise.all(
      Array.from(ownersToReconcile).map((ownerId) => reconcileActiveSharesCount(ownerId))
    );

    console.log(`🟢 Cleaned up ${count} expired shares`);
    return count;
  } catch (error) {
    console.error('🔴 Error cleaning up expired shares:', error);
    return 0;
  }
};

/**
 * Clean up shares that hit max views
 */
export const cleanupMaxedShares = async (): Promise<number> => {
  try {
    console.log('🟡 Cleaning up maxed-out shares...');

    const sharesRef = collection(db, 'sharedSessions');
    const querySnapshot = await getDocs(sharesRef);

    const batch = writeBatch(db);
    let count = 0;
    const ownersToReconcile = new Set<string>();

    querySnapshot.forEach((shareDoc) => {
      const data = shareDoc.data();
      const maxViews = data.maxViews || SHARE_LIMITS.MAX_VIEWS_PER_SHARE;

      if (data.isActive && data.accessCount >= maxViews) {
        if (typeof data.ownerId === 'string' && data.ownerId) {
          ownersToReconcile.add(data.ownerId);
        }

        batch.update(shareDoc.ref, {
          isActive: false,
          deactivatedReason: 'max_views' as ShareDeactivationReason,
          deactivatedAt: Timestamp.now(),
          mediaAccessToken: deleteField(),
          mediaAccessTokenExpiresAt: deleteField()
        });
        count++;
      }
    });

    if (count > 0) {
      await batch.commit();
      await Promise.all(
        Array.from(ownersToReconcile).map((ownerId) => reconcileActiveSharesCount(ownerId))
      );
    }

    console.log(`🟢 Cleaned up ${count} maxed-out shares`);
    return count;
  } catch (error) {
    console.error('🔴 Error cleaning up maxed shares:', error);
    return 0;
  }
};

/**
 * Get all shares for a user (active and inactive)
 */
export const getUserShares = async (userId: string): Promise<SharedSession[]> => {
  try {
    const sharesRef = collection(db, 'sharedSessions');
    const q = query(sharesRef, where('ownerId', '==', userId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((shareDoc) => ({
      shareId: shareDoc.id,
      ...shareDoc.data()
    } as SharedSession));
  } catch (error) {
    console.error('🔴 Error fetching user shares:', error);
    return [];
  }
};

/**
 * Get active share for a specific video
 */
export const getActiveShareForVideo = async (
  userId: string,
  videoId: string
): Promise<SharedSession | null> => {
  try {
    const sharesRef = collection(db, 'sharedSessions');
    const q = query(
      sharesRef,
      where('ownerId', '==', userId),
      where('videoId', '==', videoId),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const shareDoc = querySnapshot.docs[0];
    return {
      shareId: shareDoc.id,
      ...shareDoc.data()
    } as SharedSession;
  } catch (error) {
    console.error('🔴 Error fetching active share:', error);
    return null;
  }
};

/**
 * Delete a share permanently
 */
export const deleteShare = async (shareId: string, userId: string): Promise<void> => {
  try {
    const shareRef = doc(db, 'sharedSessions', shareId);
    const shareDoc = await getDoc(shareRef);

    if (!shareDoc.exists()) {
      throw new Error('Share not found');
    }

    const data = shareDoc.data();

    // Verify ownership
    if (data.ownerId !== userId) {
      throw new Error('Not authorized to delete this share');
    }

    await deleteDoc(shareRef);
    await reconcileActiveSharesCount(userId);

    console.log('🟢 Share deleted:', shareId);
  } catch (error) {
    console.error('🔴 Error deleting share:', error);
    throw error;
  }
};
